import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  TicketIcon,
  RefreshCwIcon,
  Trash2Icon,
  XIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  LayoutGridIcon,
  ListIcon,
} from '../components/icons.js';
import type { JiraSource, DocumentRegistryEntry } from '@kennisbank/shared';
import { ApiError, apiDelete, apiGet, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';

/**
 * Jira Sources admin page.
 *
 * Andre registers a Jira project; we crawl every issue (+ comments +
 * changelog) and run each through the same ingest pipeline the PDF/DOCX/
 * Confluence uploads use, landing them as `jira-{KEY}.md` docs in the
 * matching ring's collection. Per-source ring placement + classification.
 * The first crawl of a big project is resumable (status `partial` → Sync
 * again to continue); a daily Cloud Scheduler sweep keeps the delta fresh.
 *
 * IT internal helpdesk is intentionally NOT ingested — it stays live-only via
 * /qa, which respects per-user Jira ACLs. Don't add it as a source.
 */

interface SyncResponse {
  jobId: string;
  sourceId: string;
  status: 'queued';
}

interface DiscoverResult {
  discovered: number;
  created: Array<{ key: string; name: string }>;
  skippedExisting: string[];
  excludedServiceDesk: string[];
}

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError) return err.message ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

function relativeTime(iso: string, now: number): string {
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  if (diff < 1000) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}

function statusBadge(status: JiraSource['lastSyncStatus']): JSX.Element {
  const styles: Record<typeof status, string> = {
    idle: 'bg-nebula-gray-5 text-nebula-fg-3',
    running: 'bg-nebula-tint text-nebula-secondary',
    success: 'bg-nebula-success-bg text-nebula-success',
    partial: 'bg-nebula-warning-bg text-nebula-warning',
    failed: 'bg-nebula-error-bg text-nebula-error',
  };
  const labels: Record<typeof status, string> = {
    idle: 'Idle',
    running: 'Syncing',
    success: 'Succeeded',
    partial: 'Partial',
    failed: 'Failed',
  };
  return (
    <span
      className={`ov-meta-upper inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 ${styles[status]}`}
    >
      {status === 'running' && (
        <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-nebula-secondary" />
      )}
      {labels[status]}
    </span>
  );
}

function SyncProgressStrip({
  progress,
}: {
  progress: NonNullable<JiraSource['syncProgress']>;
}): JSX.Element {
  const total = Math.max(progress.totalIssues, 1);
  const pct = Math.min(100, Math.round((progress.currentIndex / total) * 100));
  const phaseLabel: Record<typeof progress.phase, string> = {
    listing: 'Listing',
    fetching: 'Fetching',
    ingesting: 'Ingesting',
    completed: 'Completed',
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-xs text-nebula-fg-2">
        <span className="font-semibold text-nebula-fg-1">
          Issue {progress.currentIndex}/{progress.totalIssues}
        </span>
        {progress.currentIssueKey && (
          <span className="truncate font-mono text-nebula-fg-3">— {progress.currentIssueKey}</span>
        )}
        <span className="ov-meta-upper ml-auto inline-flex items-center rounded-md bg-nebula-tint px-1.5 py-0.5 text-nebula-secondary">
          {phaseLabel[progress.phase]}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-nebula-tint">
        <div
          className="h-full bg-nebula-secondary transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="ov-meta text-nebula-fg-3">
        {progress.issuesSucceeded} succeeded · {progress.issuesUnchanged} unchanged ·{' '}
        {progress.issuesFailed} failed
      </div>
    </div>
  );
}

/** Per-row poll — GET /sources/:id every 2.5s while running. */
function useSourcePoll(id: string, enabled: boolean, onUpdate: (s: JiraSource) => void): void {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const tick = (): void => {
      apiGet<{ source: JiraSource }>(`/api/v1/admin/jira-sources/sources/${id}`)
        .then((res) => {
          if (!cancelled) onUpdate(res.source);
        })
        .catch(() => undefined);
    };
    const t = setInterval(tick, 2500);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [id, enabled, onUpdate]);
}

interface SourceCardProps {
  source: JiraSource;
  expanded: boolean;
  syncing: boolean;
  onToggleExpand: (id: string) => void;
  onSync: (id: string, force?: boolean) => void;
  onRequestDelete: (s: JiraSource) => void;
  onSourceUpdate: (s: JiraSource) => void;
  onError: (msg: string) => void;
}

const SourceCard = memo(function SourceCard({
  source: s,
  expanded,
  syncing,
  onToggleExpand,
  onSync,
  onRequestDelete,
  onSourceUpdate,
  onError,
}: SourceCardProps): JSX.Element {
  useSourcePoll(s.id, s.lastSyncStatus === 'running', onSourceUpdate);

  const stats = s.lastSyncStats;
  const ringLabel = s.ring === 'ring0' ? 'OneNebula Internal' : 'Product';
  const ringChipClass =
    s.ring === 'ring0'
      ? 'bg-nebula-gray-5 text-nebula-fg-1'
      : 'bg-nebula-tint text-nebula-secondary';
  // True lifetime indexed count from the backend (Firestore count on
  // kennisbank_ring1_documents where jiraSourceId === s.id). Falls back
  // to the legacy last-run-stats sum so old client builds against the
  // pre-fix backend don't render "0" unnecessarily.
  const issueCountHint =
    (s as { indexedCount?: number }).indexedCount ??
    (stats?.issuesSucceeded ?? 0) + (stats?.issuesUnchanged ?? 0);
  const now = Date.now();
  const lastSyncRel = s.lastSyncedAt ? relativeTime(s.lastSyncedAt, now) : null;
  const browseUrl = `https://${s.cloudBaseUrl}/jira/software/projects/${s.projectKey}/issues`;

  return (
    <div className="flex flex-col rounded-md border border-nebula-border-soft bg-nebula-surface p-5 transition-shadow hover:shadow-sm">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-nebula-tint text-nebula-secondary">
          <TicketIcon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="ov-h-card truncate text-nebula-fg-1" title={s.name}>
            {s.name}
          </h3>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="ov-meta-upper inline-flex items-center rounded-md bg-nebula-tint px-1.5 py-0.5 font-mono text-nebula-secondary">
              {s.projectKey}
            </span>
            <span
              className={`ov-meta-upper inline-flex items-center rounded-md px-1.5 py-0.5 ${ringChipClass}`}
            >
              {ringLabel}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0">{statusBadge(s.lastSyncStatus)}</div>
      </div>

      <div className="ov-meta mt-3 text-nebula-fg-3">
        <a
          href={browseUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-mono text-[12px] text-nebula-secondary hover:underline"
        >
          {s.cloudBaseUrl}
          <ExternalLinkIcon size={11} />
        </a>
        {issueCountHint > 0 && (
          <>
            {' · '}
            <span className="ov-numeric text-nebula-fg-2">{issueCountHint}</span> issues
          </>
        )}
      </div>

      <div className="ov-meta mt-1 text-nebula-fg-3">
        {lastSyncRel ? `Last sync ${lastSyncRel}` : 'Never synced'}
        {stats && (
          <>
            {' · '}
            {stats.issuesSucceeded} new
            {stats.issuesUnchanged > 0 ? ` · ${stats.issuesUnchanged} unchanged` : ''}
            {stats.issuesFailed > 0 ? ` · ${stats.issuesFailed} failed` : ''}
          </>
        )}
        {s.lastSyncStatus === 'partial' && (
          <span className="ml-1 text-nebula-warning">— more to crawl, click Sync to continue</span>
        )}
      </div>

      {s.lastSyncStatus === 'running' && s.syncProgress && (
        <div className="mt-4 rounded-md border border-nebula-border-soft bg-nebula-tint/40 px-3 py-2.5">
          <SyncProgressStrip progress={s.syncProgress} />
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onToggleExpand(s.id)}
          className="ov-meta inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-nebula-secondary hover:text-nebula-primary"
        >
          {expanded ? <ChevronDownIcon size={13} /> : <ChevronRightIcon size={13} />}
          {expanded ? 'Hide issues' : `View issues${issueCountHint ? ` (${issueCountHint})` : ''}`}
        </button>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => onSync(s.id)}
            disabled={syncing || s.lastSyncStatus === 'running'}
            title="Crawl new + changed issues since the last sync. Resumes a partial first crawl."
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-nebula-border bg-nebula-surface px-2.5 py-1.5 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
          >
            <RefreshCwIcon size={13} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing…' : 'Sync'}
          </button>
          <button
            type="button"
            onClick={() => onSync(s.id, true)}
            disabled={syncing || s.lastSyncStatus === 'running'}
            title="Force a full re-crawl from the first issue — re-renders + re-embeds every issue."
            className="inline-flex items-center whitespace-nowrap rounded-md border border-nebula-border bg-nebula-surface px-2.5 py-1.5 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
          >
            Re-crawl
          </button>
          <button
            type="button"
            onClick={() => onRequestDelete(s)}
            title="Delete source"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-nebula-border bg-nebula-surface text-nebula-error hover:bg-nebula-error-bg"
          >
            <Trash2Icon size={14} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 border-t border-nebula-border-soft pt-4">
          <SourceIssuesPanel source={s} onError={onError} />
        </div>
      )}
    </div>
  );
});

interface IssuesResponse {
  source: JiraSource;
  issues: DocumentRegistryEntry[];
  totalChunks: number;
}

function jiraKeyFromFilename(filename: string): string {
  const m = /^jira-(.+)\.md$/.exec(filename);
  return m && m[1] ? m[1].toUpperCase() : filename;
}

function SourceIssuesPanel({
  source,
  onError,
}: {
  source: JiraSource;
  onError: (msg: string) => void;
}): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState<DocumentRegistryEntry[] | null>(null);
  const [totalChunks, setTotalChunks] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<IssuesResponse>(`/api/v1/admin/jira-sources/sources/${source.id}/issues`)
      .then((res) => {
        if (cancelled) return;
        setIssues(res.issues);
        setTotalChunks(res.totalChunks);
      })
      .catch((err: unknown) => {
        if (!cancelled) onError(errorMessage(err, 'Could not load issues.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source.id, onError]);

  if (loading && issues === null) {
    return <div className="py-3 text-xs text-nebula-fg-4">Loading issues…</div>;
  }
  if (!issues || issues.length === 0) {
    return (
      <div className="py-3 text-xs text-nebula-fg-4">
        No issues ingested yet. Click Sync to crawl this project.
      </div>
    );
  }
  return (
    <div>
      <div className="ov-meta mb-2 text-nebula-fg-3">
        {issues.length} issues · {totalChunks.toLocaleString('en-GB')} chunks
      </div>
      <ul className="max-h-80 space-y-0.5 overflow-y-auto">
        {issues.map((it) => {
          const key = jiraKeyFromFilename(it.filename);
          return (
            <li
              key={it.filename}
              className="flex items-center gap-2 rounded px-2 py-1 text-[12px] leading-snug hover:bg-nebula-tint"
            >
              <a
                href={`https://${source.cloudBaseUrl}/browse/${key}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 font-mono text-nebula-secondary hover:underline"
              >
                {key}
              </a>
              <span className="flex-1 truncate text-nebula-fg-1" title={it.documentTitle}>
                {truncate(it.documentTitle || key, 90)}
              </span>
              <span className="ov-numeric flex-shrink-0 text-[11px] text-nebula-fg-4">
                {it.chunkCount} chunks
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Dense list view — same data + actions as the cards, packed into a table for
 * scanning many projects at once. Each row polls itself while syncing so the
 * status pill + counters animate without re-rendering the whole table.
 */
function JiraSourceList({
  sources,
  syncing,
  onSync,
  onRequestDelete,
  onSourceUpdate,
}: {
  sources: JiraSource[];
  syncing: Record<string, boolean>;
  onSync: (id: string, force?: boolean) => void;
  onRequestDelete: (s: JiraSource) => void;
  onSourceUpdate: (s: JiraSource) => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-nebula-border-soft bg-nebula-surface">
      <table className="w-full min-w-[760px] text-sm">
        <thead className="border-b border-nebula-border bg-nebula-gray-6">
          <tr>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Project</th>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Issues</th>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Last sync</th>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Status</th>
            <th className="ov-meta-upper px-3 py-2 text-right text-nebula-fg-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((s) => (
            <JiraSourceRow
              key={s.id}
              source={s}
              syncing={!!syncing[s.id]}
              onSync={onSync}
              onRequestDelete={onRequestDelete}
              onSourceUpdate={onSourceUpdate}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

const JiraSourceRow = memo(function JiraSourceRow({
  source: s,
  syncing,
  onSync,
  onRequestDelete,
  onSourceUpdate,
}: {
  source: JiraSource;
  syncing: boolean;
  onSync: (id: string, force?: boolean) => void;
  onRequestDelete: (s: JiraSource) => void;
  onSourceUpdate: (s: JiraSource) => void;
}): JSX.Element {
  useSourcePoll(s.id, s.lastSyncStatus === 'running', onSourceUpdate);
  const stats = s.lastSyncStats;
  const issueCount =
    (s as { indexedCount?: number }).indexedCount ??
    (stats?.issuesSucceeded ?? 0) + (stats?.issuesUnchanged ?? 0);
  const lastSyncRel = s.lastSyncedAt ? relativeTime(s.lastSyncedAt, Date.now()) : '—';
  const disabled = syncing || s.lastSyncStatus === 'running';
  return (
    <tr className="border-b border-nebula-border-soft last:border-b-0 hover:bg-nebula-tint">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="ov-meta-upper inline-flex items-center rounded bg-nebula-tint px-1.5 py-0.5 font-mono text-[11px] text-nebula-secondary">
            {s.projectKey}
          </span>
          <span className="font-medium text-nebula-fg-1" title={s.name}>
            {truncate(s.name, 48)}
          </span>
        </div>
        <div className="ov-meta mt-0.5 truncate font-mono text-[11px] text-nebula-fg-3">
          {s.cloudBaseUrl}
        </div>
      </td>
      <td className="ov-numeric px-3 py-2 text-nebula-fg-1">
        {issueCount > 0 ? issueCount.toLocaleString('en-GB') : '—'}
      </td>
      <td className="ov-meta px-3 py-2 text-nebula-fg-2">{lastSyncRel}</td>
      <td className="px-3 py-2">{statusBadge(s.lastSyncStatus)}</td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => onSync(s.id)}
            disabled={disabled}
            title="Sync new + changed issues"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-nebula-border bg-nebula-surface px-2.5 py-1 text-xs font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
          >
            <RefreshCwIcon size={12} className={syncing ? 'animate-spin' : ''} />
            Sync
          </button>
          <button
            type="button"
            onClick={() => onSync(s.id, true)}
            disabled={disabled}
            title="Force a full re-crawl"
            className="inline-flex items-center whitespace-nowrap rounded-md border border-nebula-border bg-nebula-surface px-2.5 py-1 text-xs font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
          >
            Re-crawl
          </button>
          <button
            type="button"
            onClick={() => onRequestDelete(s)}
            title="Delete source"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-nebula-border bg-nebula-surface text-nebula-error hover:bg-nebula-error-bg"
          >
            <Trash2Icon size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
});

export function AdminJiraSourcesPage(): JSX.Element {
  const [sources, setSources] = useState<JiraSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const [pendingDelete, setPendingDelete] = useState<JiraSource | null>(null);
  const [reload, setReload] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [discovering, setDiscovering] = useState(false);
  const [discoverResult, setDiscoverResult] = useState<DiscoverResult | null>(null);
  // View toggle (cards / list), persisted so a power user doesn't re-flip it
  // every load. Mirrors the Confluence sources page.
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    if (typeof window === 'undefined') return 'cards';
    const stored = window.localStorage.getItem('kb.jira.viewMode');
    return stored === 'list' || stored === 'cards' ? stored : 'cards';
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('kb.jira.viewMode', viewMode);
    } catch {
      // localStorage can throw in private mode; ignore.
    }
  }, [viewMode]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);
  const toggleExpanded = useCallback(
    (id: string) => setExpanded((s) => ({ ...s, [id]: !s[id] })),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTopError(null);
    apiGet<{ sources: JiraSource[] }>('/api/v1/admin/jira-sources/sources')
      .then((res) => {
        if (!cancelled) setSources(res.sources);
      })
      .catch((err: unknown) => {
        if (!cancelled) setTopError(errorMessage(err, 'Could not load Jira sources.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const replaceSource = useCallback((next: JiraSource) => {
    setSources((prev) => {
      const idx = prev.findIndex((s) => s.id === next.id);
      if (idx < 0 || prev[idx] === next) return prev;
      const copy = prev.slice();
      copy[idx] = next;
      return copy;
    });
  }, []);

  const onSync = useCallback(
    async (id: string, force = false) => {
      setSyncing((s) => ({ ...s, [id]: true }));
      try {
        const url = force
          ? `/api/v1/admin/jira-sources/sources/${id}/sync?force=true`
          : `/api/v1/admin/jira-sources/sources/${id}/sync`;
        await apiPost<SyncResponse>(url, {});
        apiGet<{ source: JiraSource }>(`/api/v1/admin/jira-sources/sources/${id}`)
          .then((res) => replaceSource(res.source))
          .catch(() => undefined);
      } catch (err) {
        setTopError(errorMessage(err, 'Sync failed.'));
      } finally {
        setSyncing((s) => ({ ...s, [id]: false }));
      }
    },
    [replaceSource],
  );

  const onDelete = useCallback(
    async (source: JiraSource) => {
      try {
        await apiDelete(`/api/v1/admin/jira-sources/sources/${source.id}`);
        setPendingDelete(null);
        refresh();
      } catch (err) {
        setTopError(errorMessage(err, 'Delete failed.'));
      }
    },
    [refresh],
  );

  const onDiscover = useCallback(async () => {
    setDiscovering(true);
    setTopError(null);
    try {
      const res = await apiPost<DiscoverResult>('/api/v1/admin/jira-sources/sources/discover', {});
      setDiscoverResult(res);
      refresh();
    } catch (err) {
      setTopError(errorMessage(err, 'Project discovery failed.'));
    } finally {
      setDiscovering(false);
    }
  }, [refresh]);

  const kpis = useMemo(() => {
    let totalIssues = 0;
    let activeSyncs = 0;
    for (const s of sources) {
      const stats = s.lastSyncStats;
      const indexed = (s as { indexedCount?: number }).indexedCount;
      totalIssues +=
        typeof indexed === 'number'
          ? indexed
          : (stats?.issuesSucceeded ?? 0) + (stats?.issuesUnchanged ?? 0);
      if (s.lastSyncStatus === 'running') activeSyncs += 1;
    }
    return { totalSources: sources.length, totalIssues, activeSyncs };
  }, [sources]);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="ov-h-page">Jira Sources</h1>
            <p className="ov-body mt-1 text-nebula-fg-3">
              Ingest Jira projects (issues + comments + history) into the knowledgebase. The IT
              internal helpdesk is intentionally excluded — keep it live-only via <code>/qa</code>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div
              role="group"
              aria-label="View mode"
              className="inline-flex overflow-hidden rounded-md border border-nebula-border bg-nebula-surface"
            >
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                aria-pressed={viewMode === 'cards'}
                title="Card view"
                className={`inline-flex h-9 w-9 items-center justify-center transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-nebula-primary text-white'
                    : 'text-nebula-fg-2 hover:bg-nebula-tint'
                }`}
              >
                <LayoutGridIcon size={16} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                aria-pressed={viewMode === 'list'}
                title="List view"
                className={`inline-flex h-9 w-9 items-center justify-center border-l border-nebula-border transition-colors ${
                  viewMode === 'list'
                    ? 'bg-nebula-primary text-white'
                    : 'text-nebula-fg-2 hover:bg-nebula-tint'
                }`}
              >
                <ListIcon size={16} />
              </button>
            </div>
            <button
              type="button"
              onClick={onDiscover}
              disabled={discovering}
              title="List every Jira project you can access and add each one (service-desk/helpdesk projects are excluded)."
              className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-nebula-surface px-4 py-2 font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
            >
              <RefreshCwIcon size={15} className={discovering ? 'animate-spin' : ''} />
              {discovering ? 'Discovering…' : 'Discover projects'}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="rounded-md bg-nebula-primary px-4 py-2 font-ui text-white hover:bg-nebula-primary/90"
            >
              Connect project
            </button>
          </div>
        </header>

        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiTile
            label="Projects"
            value={
              loading && sources.length === 0 ? '—' : kpis.totalSources.toLocaleString('en-GB')
            }
          />
          <KpiTile
            label="Issues ingested"
            value={
              loading && sources.length === 0
                ? '—'
                : kpis.totalIssues > 0
                  ? kpis.totalIssues.toLocaleString('en-GB')
                  : '—'
            }
          />
          <KpiTile
            label="Active syncs"
            value={loading && sources.length === 0 ? '—' : kpis.activeSyncs.toLocaleString('en-GB')}
            sub={kpis.activeSyncs > 0 ? 'Syncing now' : 'Idle'}
          />
        </div>

        {topError && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-nebula-error/20 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
            <span>{topError}</span>
            <button
              type="button"
              onClick={() => setTopError(null)}
              aria-label="Close error message"
              className="text-nebula-error/60 hover:text-nebula-error"
            >
              <XIcon size={14} />
            </button>
          </div>
        )}

        {loading ? (
          <div className="ov-body text-nebula-fg-3">Loading…</div>
        ) : sources.length === 0 ? (
          <div className="rounded-md border border-dashed border-nebula-border bg-nebula-surface p-10 text-center">
            <TicketIcon size={28} className="mx-auto text-nebula-fg-4" />
            <p className="ov-body mt-3 text-nebula-fg-3">
              No Jira projects configured. Click &quot;Connect project&quot; to start.
            </p>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sources.map((s) => (
              <SourceCard
                key={s.id}
                source={s}
                expanded={!!expanded[s.id]}
                syncing={!!syncing[s.id]}
                onToggleExpand={toggleExpanded}
                onSync={onSync}
                onRequestDelete={setPendingDelete}
                onSourceUpdate={replaceSource}
                onError={setTopError}
              />
            ))}
          </div>
        ) : (
          <JiraSourceList
            sources={sources}
            syncing={syncing}
            onSync={onSync}
            onRequestDelete={setPendingDelete}
            onSourceUpdate={replaceSource}
          />
        )}
      </div>

      {showAdd && (
        <AddSourceModal
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            refresh();
          }}
          onError={setTopError}
        />
      )}

      {discoverResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4">
          <div className="w-full max-w-lg rounded-md bg-nebula-surface p-6 shadow-lg">
            <div className="flex items-start justify-between">
              <h2 className="ov-h-section">Project discovery</h2>
              <button
                type="button"
                onClick={() => setDiscoverResult(null)}
                aria-label="Close"
                className="rounded-md p-1 text-nebula-fg-3 hover:bg-nebula-tint"
              >
                <XIcon size={18} />
              </button>
            </div>
            <p className="ov-body mt-2 text-nebula-fg-3">
              Found {discoverResult.discovered} accessible project
              {discoverResult.discovered === 1 ? '' : 's'}. Added{' '}
              <strong className="text-nebula-fg-1">{discoverResult.created.length}</strong> new
              source{discoverResult.created.length === 1 ? '' : 's'}.
            </p>
            <div className="mt-4 space-y-3 text-sm">
              {discoverResult.created.length > 0 && (
                <div>
                  <div className="ov-meta-upper text-nebula-success">
                    Added ({discoverResult.created.length})
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {discoverResult.created.map((c) => (
                      <span
                        key={c.key}
                        title={c.name}
                        className="rounded bg-nebula-success-bg px-1.5 py-0.5 font-mono text-[11px] text-nebula-success"
                      >
                        {c.key}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {discoverResult.skippedExisting.length > 0 && (
                <div>
                  <div className="ov-meta-upper text-nebula-fg-3">
                    Already configured ({discoverResult.skippedExisting.length})
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {discoverResult.skippedExisting.map((k) => (
                      <span
                        key={k}
                        className="rounded bg-nebula-gray-5 px-1.5 py-0.5 font-mono text-[11px] text-nebula-fg-2"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {discoverResult.excludedServiceDesk.length > 0 && (
                <div>
                  <div className="ov-meta-upper text-nebula-warning">
                    Excluded — service desk / helpdesk ({discoverResult.excludedServiceDesk.length})
                  </div>
                  <p className="ov-meta mt-0.5 text-nebula-fg-3">
                    Service-desk projects are kept out of the index (HR-PII) — they stay live-only
                    via <code>/qa</code>.
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {discoverResult.excludedServiceDesk.map((k) => (
                      <span
                        key={k}
                        className="rounded bg-nebula-warning-bg px-1.5 py-0.5 font-mono text-[11px] text-nebula-warning"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setDiscoverResult(null)}
                className="rounded-md bg-nebula-primary px-4 py-2 font-ui text-white hover:bg-nebula-primary/90"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4">
          <div className="w-full max-w-md rounded-md bg-nebula-surface p-6 shadow-lg">
            <h2 className="ov-h-section">Delete source?</h2>
            <p className="ov-body mt-2 text-nebula-fg-3">
              Removes the registration for &quot;{pendingDelete.name}&quot;. Already-ingested issues
              stay in the knowledgebase — delete those individually from /documenten if needed.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onDelete(pendingDelete)}
                className="rounded-md bg-nebula-error px-4 py-2 font-ui text-white hover:bg-nebula-error/90"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function KpiTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-nebula-border-soft bg-nebula-surface p-4 transition-shadow hover:shadow-sm">
      <div className="ov-meta-upper text-nebula-fg-3">{label}</div>
      <div className="ov-numeric mt-2 text-3xl text-nebula-fg-1">{value}</div>
      {sub && <div className="ov-meta mt-1 text-nebula-fg-3">{sub}</div>}
    </div>
  );
}

function AddSourceModal({
  onClose,
  onCreated,
  onError,
}: {
  onClose: () => void;
  onCreated: () => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [cloudBaseUrl, setCloudBaseUrl] = useState('onenebula.atlassian.net');
  const [ring, setRing] = useState<'ring0' | 'ring1'>('ring1');
  const [defaultClassification, setDefaultClassification] = useState<
    'public' | 'internal' | 'confidential' | 'restricted'
  >('internal');
  const [defaultAccessGroups, setDefaultAccessGroups] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const requiresGroup =
    defaultClassification === 'confidential' || defaultClassification === 'restricted';
  const groupError = requiresGroup && defaultAccessGroups.length === 0;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim() || !projectKey.trim()) return;
    if (groupError) {
      onError('Confidential or Restricted classification requires at least one access group.');
      return;
    }
    setSubmitting(true);
    try {
      await apiPost('/api/v1/admin/jira-sources/sources', {
        name: name.trim(),
        projectKey: projectKey.trim().toUpperCase(),
        cloudBaseUrl: cloudBaseUrl.trim(),
        ring,
        ...(defaultClassification !== 'internal' ? { defaultClassification } : {}),
        ...(defaultAccessGroups.length > 0 ? { defaultAccessGroups } : {}),
      });
      onCreated();
    } catch (err) {
      onError(errorMessage(err, 'Could not create source.'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'mt-1 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none focus:ring-2 focus:ring-nebula-secondary/20';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-md bg-nebula-surface p-6 shadow-lg"
      >
        <div className="flex items-start justify-between">
          <h2 className="ov-h-section">Connect Jira project</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-nebula-fg-3 hover:bg-nebula-tint"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="jira-name">
              Name
            </label>
            <input
              id="jira-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              placeholder="OneKB Engineering"
            />
          </div>
          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="jira-key">
              Project key
            </label>
            <input
              id="jira-key"
              required
              value={projectKey}
              onChange={(e) => setProjectKey(e.target.value.toUpperCase())}
              className={`${inputCls} font-mono`}
              placeholder="OK"
            />
            <p className="ov-meta mt-1 text-nebula-fg-3">
              Upper-case key from the issue prefix (e.g. <code>OK</code> for <code>OK-82</code>).
            </p>
          </div>
          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="jira-host">
              Cloud host
            </label>
            <input
              id="jira-host"
              required
              value={cloudBaseUrl}
              onChange={(e) => setCloudBaseUrl(e.target.value)}
              className={inputCls}
              placeholder="onenebula.atlassian.net"
            />
            <p className="ov-meta mt-1 text-nebula-fg-3">No protocol, no trailing slash.</p>
          </div>
          <fieldset>
            <legend className="ov-meta-upper text-nebula-fg-2">Ring</legend>
            <div className="mt-2 space-y-2">
              <label className="ov-body flex items-start gap-2 text-nebula-fg-1">
                <input
                  type="radio"
                  name="ring"
                  checked={ring === 'ring1'}
                  onChange={() => setRing('ring1')}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-semibold">Product</span> (ring1) — broader access
                </span>
              </label>
              <label className="ov-body flex items-start gap-2 text-nebula-fg-1">
                <input
                  type="radio"
                  name="ring"
                  checked={ring === 'ring0'}
                  onChange={() => setRing('ring0')}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-semibold">Internal</span> (ring0) — internal staff only
                </span>
              </label>
            </div>
          </fieldset>
          <fieldset>
            <legend className="ov-meta-upper text-nebula-fg-2">Default classification</legend>
            <p className="ov-meta mt-1 text-nebula-fg-3">
              Cascaded onto every issue ingested from this project. Operators can downgrade per-doc
              later from /documenten.
            </p>
            <select
              value={defaultClassification}
              onChange={(e) =>
                setDefaultClassification(
                  e.target.value as 'public' | 'internal' | 'confidential' | 'restricted',
                )
              }
              className={inputCls}
            >
              <option value="public">public</option>
              <option value="internal">internal</option>
              <option value="confidential">confidential</option>
              <option value="restricted">restricted</option>
            </select>
            {requiresGroup && (
              <label className="ov-body mt-2 flex items-center gap-2 text-nebula-fg-1">
                <input
                  type="checkbox"
                  checked={defaultAccessGroups.includes('confidential')}
                  onChange={(e) =>
                    setDefaultAccessGroups(
                      e.target.checked
                        ? Array.from(new Set([...defaultAccessGroups, 'confidential']))
                        : defaultAccessGroups.filter((g) => g !== 'confidential'),
                    )
                  }
                />
                <span>Access group: confidential (Nebula Exco members)</span>
              </label>
            )}
            {groupError && (
              <p className="ov-meta mt-1 text-nebula-error">
                Confidential or Restricted requires at least one access group.
              </p>
            )}
          </fieldset>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-nebula-primary px-4 py-2 font-ui text-white hover:bg-nebula-primary/90 disabled:opacity-60"
          >
            {submitting ? 'Creating…' : 'Create source'}
          </button>
        </div>
      </form>
    </div>
  );
}
